import { PATIENT_SUMMARY_TEMPLATE_CATEGORY } from '@heidi/patients/contracts';
import { TemplateCategory } from '@heidi/schema/ml-scribe';
import type { FilterSettings } from '@heidi/templates/contracts';
import { TemplateCommunitySortBy } from '@heidi/templates/contracts';
import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';

export const communityFiltersMessages = defineMessages({
  sort: {
    defaultMessage: 'Sort',
    id: 'H2Oq3Y00RO',
    description: 'Label for the community template sort filter',
  },
  location: {
    defaultMessage: 'Location',
    id: 'Ddg+jZaGe+',
    description: 'Label for the community template location filter',
  },
  specialty: {
    defaultMessage: 'Specialty',
    id: 'MHLEmJ9Bic',
    description: 'Label for the community template specialty filter',
  },
  category: {
    defaultMessage: 'Category',
    id: 'jbvEs8BJoM',
    description: 'Label for the community template category filter',
  },
  all: {
    defaultMessage: 'All',
    id: '5gEMKBuqXG',
    description: 'Option to show all items in a community template filter',
  },
  resetFilters: {
    defaultMessage: 'Reset filters',
    id: 'qo48g7TTAt',
    description: 'Action to clear active community template filters',
  },
  note: {
    defaultMessage: 'Note',
    id: 'Tv/rgKQAbF',
    description: 'Consult note template category filter label',
  },
  document: {
    defaultMessage: 'Document',
    id: 'gHjaoyydzJ',
    description: 'Document template category filter label',
  },
  form: {
    defaultMessage: 'Form',
    id: 'U0AoHZkftD',
    description: 'Form template category filter label',
  },
  letter: {
    defaultMessage: 'Letter',
    id: 'Cg4PFa7PKh',
    description: 'Letter template category filter label',
  },
  assessment: {
    defaultMessage: 'Assessment',
    id: '/iSbSgIKC6',
    description: 'Assessment template category filter label',
  },
  prechart: {
    defaultMessage: 'Prechart',
    id: 'ClKQcB17ki',
    description: 'Prechart template category filter label',
  },
  patientSummary: {
    defaultMessage: 'Patient summary',
    id: '9Vm/K/v7YJ',
    description: 'Patient summary template category filter label',
  },
  mostPopular: {
    defaultMessage: 'Most Popular',
    id: 'vQhUMhnqG0',
    description: 'Sort community templates by popularity',
  },
  mostRecent: {
    defaultMessage: 'Most Recent',
    id: 'lteLEWY1XE',
    description: 'Sort community templates by recency',
  },
  alphabetical: {
    defaultMessage: 'Alphabetical',
    id: '3tUKO7WP7T',
    description: 'Sort community templates alphabetically',
  },
});

export type TemplateCategoryFilterKey = TemplateCategory;

export const TEMPLATE_CATEGORY_FILTER_MESSAGES: Record<
  TemplateCategoryFilterKey,
  MessageDescriptor
> = {
  [TemplateCategory.CONSULT_NOTE_TEMPLATE]: communityFiltersMessages.note,
  [TemplateCategory.DOCUMENT_TEMPLATE]: communityFiltersMessages.document,
  [TemplateCategory.FORM_TEMPLATE]: communityFiltersMessages.form,
  [TemplateCategory.LETTER_TEMPLATE]: communityFiltersMessages.letter,
  [TemplateCategory.ASSESSMENT_TEMPLATE]: communityFiltersMessages.assessment,
  [TemplateCategory.PRECHART_TEMPLATE]: communityFiltersMessages.prechart,
  [PATIENT_SUMMARY_TEMPLATE_CATEGORY]: communityFiltersMessages.patientSummary,
};

export const TEMPLATE_COMMUNITY_SORT_MESSAGES: Record<
  TemplateCommunitySortBy,
  MessageDescriptor
> = {
  [TemplateCommunitySortBy.MOST_POPULAR]: communityFiltersMessages.mostPopular,
  [TemplateCommunitySortBy.MOST_RECENT]: communityFiltersMessages.mostRecent,
  [TemplateCommunitySortBy.ALPHABETICAL]: communityFiltersMessages.alphabetical,
};

export function getTemplateCategoryFilterLabel(
  intl: IntlShape,
  category: TemplateCategoryFilterKey
): string {
  return intl.formatMessage(TEMPLATE_CATEGORY_FILTER_MESSAGES[category]);
}

export function getTemplateCommunitySortLabel(
  intl: IntlShape,
  sort: TemplateCommunitySortBy
): string {
  return intl.formatMessage(TEMPLATE_COMMUNITY_SORT_MESSAGES[sort]);
}

export function getCommunityFilterAllLabel(intl: IntlShape): string {
  return intl.formatMessage(communityFiltersMessages.all);
}

export function isTemplateCategoryFilterKey(
  value: string | FilterSettings
): value is TemplateCategoryFilterKey {
  return value in TEMPLATE_CATEGORY_FILTER_MESSAGES;
}
